import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";
import { Loader, PageHeader } from "@/components/common/Primitives";

export default function AdminRazorpay() {
  const [s, setS] = useState(null);
  const [secret, setSecret] = useState("");
  const [wh, setWh] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  useEffect(() => { api.get("/admin/settings/razorpay").then((r) => { setS(r.data); setSaved(r.data); }); }, []);
  if (!s) return <Loader />;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch("/admin/settings/razorpay", { enabled: s.enabled, mode: s.mode, key_id: s.key_id, key_secret: secret, webhook_secret: wh });
      setS(data); setSaved(data); setSecret(""); setWh("");
      toast.success("Payment settings saved");
    } catch (err) { toast.error(apiError(err)); setS({ ...s, enabled: saved.enabled }); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl">
      <PageHeader title="Razorpay settings" subtitle="Restaurants pay their Smart Menu subscription through your Razorpay account." />
      <div className={`mb-6 rounded-2xl border p-4 text-sm ${saved.enabled ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`} data-testid="razorpay-status">
        {saved.enabled ? `Online payments are ENABLED in ${saved.mode.toUpperCase()} mode.` : "Online payments are DISABLED. Enter your Razorpay keys and enable to start accepting subscription payments."}
      </div>
      <form onSubmit={save} className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6">
        <div className="grid grid-cols-2 gap-2">
          {["test", "live"].map((m) => (
            <button type="button" key={m} onClick={() => setS({ ...s, mode: m })} data-testid={`rzp-mode-${m}`}
              className={`rounded-xl border px-3 py-2 text-sm capitalize transition-colors ${s.mode === m ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200"}`}>{m} mode</button>
          ))}
        </div>
        <label className="block text-sm font-medium text-slate-700">Key ID
          <Input value={s.key_id} onChange={(e) => setS({ ...s, key_id: e.target.value })} placeholder={s.mode === "live" ? "rzp_live_..." : "rzp_test_..."} data-testid="rzp-key-id" />
        </label>
        <label className="block text-sm font-medium text-slate-700">Key Secret {s.has_key_secret && <span className="text-xs font-normal text-emerald-600">(saved — leave blank to keep)</span>}
          <Input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={s.has_key_secret ? "Saved — leave blank to keep" : "Not set"} data-testid="rzp-key-secret" />
        </label>
        <label className="block text-sm font-medium text-slate-700">Webhook Secret {s.has_webhook_secret && <span className="text-xs font-normal text-emerald-600">(saved — leave blank to keep)</span>}
          <Input type="password" value={wh} onChange={(e) => setWh(e.target.value)} placeholder={s.has_webhook_secret ? "Saved — leave blank to keep" : "Not set"} data-testid="rzp-webhook-secret" />
        </label>
        <div className="rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
          <p className="mb-1 font-semibold">Webhook URL (add in Razorpay Dashboard → Webhooks, events: payment.captured, payment.failed, order.paid)</p>
          <div className="flex items-center gap-2">
            <code className="break-all" data-testid="rzp-webhook-url">{s.webhook_url}</code>
            <button type="button" onClick={() => { navigator.clipboard.writeText(s.webhook_url); toast.success("Copied"); }} data-testid="rzp-webhook-copy"><Copy className="h-4 w-4" /></button>
          </div>
        </div>
        <label className="flex items-center gap-3 text-sm"><Switch checked={s.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} data-testid="rzp-enabled" />Enable online subscription payments</label>
        <p className="flex items-center gap-2 text-xs text-slate-400"><ShieldCheck className="h-4 w-4" />Secrets are stored server-side only and never sent to browsers.</p>
        <Button type="submit" disabled={busy} className="bg-emerald-500 hover:bg-emerald-600" data-testid="rzp-save-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save settings</Button>
      </form>
    </div>
  );
}
